import PropTypes from 'prop-types';
import { Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Flex,
  Skeleton,
  SkeletonText,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Tooltip,
  Button,
  useDisclosure,
  LinkBox,
} from '@chakra-ui/react';
import Breadcrumbs from 'components/common/Breadcrumbs';
import { ReducerStatus } from 'utils/constants';
import PublishTag from 'components/common/fields/PublishTag';
import EmailGroup from 'components/common/fields/EmailGroup';
import IdTag from 'components/common/fields/IdTag';
import { QuestionIcon } from '@chakra-ui/icons';
import { FaUnlink } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import { unbindClassFromLearningPlanAction } from 'store/modules/learningPlan/learningPlan-action';
import PublishButton from 'components/common/buttons/PublishButton';

function LearningPlanHeader({ status, learningPlan, ...rest }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dispatch = useDispatch();

  const unbindClassFromLearningPlan = useSelector(
    (state) => state.learningPlan.unbindClassFromLearningPlan
  );

  const [selectedClassId, setSelectedClassId] = useState(null);

  const handleUnbindClass = (classId) => {
    setSelectedClassId(classId);
    onOpen();
  };

  const handleConfirmUnbind = () => {
    dispatch(
      unbindClassFromLearningPlanAction({
        learningPlanId: learningPlan?.id,
        classId: selectedClassId,
      })
    );
  };

  const handleSubmitResult = () => {
    onClose();
    setSelectedClassId(null);
  };

  return (
    <Box direction={{ base: 'column', sm: 'row' }} {...rest}>
      <ConfirmationActionModal
        onClose={onClose}
        onConfirm={handleConfirmUnbind}
        onSubmitResult={handleSubmitResult}
        isVisible={isOpen}
        status={unbindClassFromLearningPlan.status}
        description={`Are you sure you want to unbind this class from the learning plan "${learningPlan?.name}"? Once unbound, the class will no longer have access to this learning plan.`}
        successMessage="Class unbound successfully."
        error={unbindClassFromLearningPlan.error}
      />
      <Breadcrumbs
        mb={4}
        links={[
          { name: 'Learning Plans', path: '/admin/learning-plans' },
          {
            name: learningPlan?.name || 'Loading...',
            path: `/admin/learning-plan/${learningPlan?.id}`,
          },
        ]}
      />

      <VStack gap={4} align="start">
        <Skeleton
          height="30px"
          width="100%"
          isLoaded={status !== ReducerStatus.LOADING}
        >
          <Flex gap={2} align="center">
            <IdTag id={learningPlan?.id} />
            <Text fontWeight={500} fontSize="xl">
              {learningPlan?.name}
            </Text>
            <PublishButton
              entityType="LearningPlan"
              entityId={learningPlan?.id}
              isPublished={learningPlan?.isPublished}
            />
          </Flex>
        </Skeleton>

        <SkeletonText
          noOfLines={4}
          skeletonHeight="4"
          isLoaded={status !== ReducerStatus.LOADING}
        >
          <Text fontSize="lg">{learningPlan?.description}</Text>
        </SkeletonText>

        <SkeletonText noOfLines={2} isLoaded={status !== ReducerStatus.LOADING}>
          <Text fontSize="xs" fontWeight={500}>
            Owner: {learningPlan?.owner?.name || 'N/A'}
          </Text>
        </SkeletonText>

        <SkeletonText
          w="100%"
          noOfLines={5}
          isLoaded={status !== ReducerStatus.LOADING}
        >
          <EmailGroup name="Contributors:" emails={learningPlan?.ownerGroups} />
        </SkeletonText>

        <SkeletonText
          w="100%"
          noOfLines={5}
          isLoaded={status !== ReducerStatus.LOADING}
        >
          <Box w="100%" fontSize="xs">
            <Flex gap={1} align="center">
              <Text fontWeight={500}>Classes:</Text>
              <Tooltip label="Classes currently using this learning plan">
                <QuestionIcon boxSize={3} color="gray" />
              </Tooltip>
            </Flex>

            <UnorderedList ml={5}>
              {learningPlan?.classes.map((classItem) => (
                <ListItem key={classItem.id} m={2}>
                  <Flex gap={2} align="center">
                    <LinkBox
                      as={ReactRouterLink}
                      color="blue.500"
                      to={`/admin/classes/${classItem.id}`}
                    >
                      {classItem.name}
                    </LinkBox>

                    <PublishTag isPublished={classItem?.isPublished} />
                    <Tooltip label="Unbind this class from the learning plan">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => handleUnbindClass(classItem.id)}
                      >
                        <FaUnlink />
                      </Button>
                    </Tooltip>
                  </Flex>
                </ListItem>
              ))}
            </UnorderedList>
          </Box>
        </SkeletonText>
      </VStack>
    </Box>
  );
}

LearningPlanHeader.defaultProps = {
  learningPlan: null,
};

LearningPlanHeader.propTypes = {
  status: PropTypes.string.isRequired,
  learningPlan: PropTypes.object,
};

export default LearningPlanHeader;
